import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Sparkles, Trophy, TrendingUp, ArrowUp, Download, FileText, Building2, Mail } from 'lucide-react';

const ReleaseNotes = () => {
  const releases = [
    {
      version: '1.5.1', date: 'October 28, 2026', title: 'Prompts Focused on Buying Your Service', type: 'improvement',
      description: 'Generate questions about choosing the services you sell, with a tighter default scope and less generic informational content.',
      items: [{title:'Service and Buying Intent',icon:<Building2 className="h-5 w-5 text-purple-600" />,description:'Choose the service before generating questions.',details:[
        'Start with the primary approved service instead of combining every topic and audience',
        'Prioritise provider discovery, comparisons, pricing, packages and buyer suitability',
        'Offer service explainers only as an optional mix capped at 20% of saved drafts',
        'Require each generated question to reference its selected service and show its buying intent',
        'Apply the same focus to adaptation; retain existing drafts and tracking history for review',
        'Save fewer relevant drafts when checks reject suggestions, without automatic paid retries'
      ]}]
    },
    {
      version: '1.5.0',
      date: 'October 28, 2026',
      title: 'Research to Tested Content Plans',
      type: 'feature',
      description: 'Connect strategic prompt research, saved answer insights, controlled content tests and editorial approval in AI Workflow.',
      items: [
        {title:'Research & Adaptive Prompt Libraries',icon:<Building2 className="h-5 w-5 text-purple-600" />,description:'Broaden company research and steer new questions around your business priorities.',details:[
          'Research the market with one web search and up to five cited public source pages',
          'Review supported competitor names and source dates before approving research',
          'Choose up to 20, 100 or 200 drafts, saved in separate batches of 20',
          'Adapt new drafts from recent saved answers without replacing tracked prompt history',
          'Steer by audience, product, region, competitor, claim and buying stage; filter drafts and insights'
        ]},
        {title:'Answer Evidence & Content Testing',icon:<TrendingUp className="h-5 w-5 text-blue-600" />,description:'Inspect aggregate mentions and perceptions, then test concrete content hypotheses.',details:[
          'Keep answer metrics separate by model, measurement version, analysis version and country',
          'Create a candidate from exact saved-answer quotations or supply a manual proposed excerpt',
          'Compare baseline and proposed excerpts on three prompts with two repetitions each',
          'Use GPT-4o mini for controlled simulations; Luna continues to collect live web-cited answers',
          'Only completed tests meeting the fixed improvement rule qualify for content review',
          'Failed and incomplete tests retain their history; simulations do not prove live visibility'
        ]},
        {title:'Governed Content Plans',icon:<FileText className="h-5 w-5 text-green-600" />,description:'Move qualified candidates through drafting, review, approval and publication records.',details:[
          'Assign an owner, market and due date; retain every saved revision',
          'Require an owner or administrator to approve content',
          'Require a new candidate and test when the proposed content changes',
          'Export the content plan as Markdown; publish through your existing content system',
          'Record the published URL and compare later real answers on matching prompt and model versions',
          'All paid work remains user-triggered and subject to shared spending limits; the existing daily tracking scheduler is unchanged'
        ]}
      ]
    },
    {
      version: '1.4.1',
      date: 'October 28, 2026',
      title: 'Clearer AI Budget Limits',
      type: 'improvement',
      description: 'Temporary spending approvals now expire automatically, with clearer messages when a limit blocks a request.',
      items: [
        {
          title: 'One-Day Allowances',
          description: 'An approved allowance applies only to its specified UTC date, then the normal daily cap resumes automatically.',
          icon: <TrendingUp className="h-5 w-5 text-blue-600" />,
          details: [
            'Daily, monthly and per-operation budget limits remain enforced',
            'Budget messages identify which limit blocked the next request',
            'Blocked requests are not sent to the AI provider',
            'Outstanding reservations continue to count until their cost is resolved'
          ]
        }
      ]
    },
    {
      version: '1.4.0',
      date: 'October 28, 2026',
      title: 'Brand Research, Prompt Tracking & Icons',
      type: 'feature',
      description: 'Build a reviewed prompt library from your website, track answers with Luna and see the evidence behind brand mentions.',
      items: [
        {
          title: 'Guided Brand Setup',
          description: 'Adding a website now takes you through research, profile review and question generation.',
          icon: <Building2 className="h-5 w-5 text-purple-600" />,
          details: [
            'Research the company homepage and up to four relevant public pages',
            'Review and edit the company profile alongside saved sources and retrieval dates',
            'Approve the profile and generate 10 or 20 buyer questions per request',
            'Import existing questions as drafts without an AI call',
            'Select questions for weekly tracking; activation does not start paid searches',
            'Generated questions are suggestions, not observed ChatGPT query-volume data'
          ]
        },
        {
          title: 'Brand Icons',
          description: 'Add an icon when creating or editing a website.',
          icon: <Sparkles className="h-5 w-5 text-blue-600" />,
          details: [
            'Use the website favicon by default, with initials if it is unavailable',
            'Upload a PNG, JPG or WebP image up to 2 MB',
            'See the saved icon on website cards and keyword pages',
            'Remove an uploaded icon to return to favicon mode'
          ]
        },
        {
          title: 'Luna Tracking & Answer Analysis',
          description: 'New tracked answers use GPT-6 Luna with web search and native source citations.',
          icon: <FileText className="h-5 w-5 text-green-600" />,
          details: [
            'Read saved answers with clickable provider citations',
            'Analyse configured brand and competitor mentions, recommendations and perception across the answer',
            'Check findings against exact passages from the saved answer',
            'Keep usable answers even when no citations are returned or analysis fails',
            'Retain prompt versions and model settings to distinguish measurement changes',
            'Research, prompt generation and analysis continue to use GPT-4o mini'
          ]
        },
        {
          title: 'Spending & Refresh Controls',
          description: 'Bounded requests and explicit refreshes keep routine AI usage under control.',
          icon: <TrendingUp className="h-5 w-5 text-blue-600" />,
          details: [
            'The scheduler checks once daily and respects each prompt\'s frequency and 24-hour attempt guard',
            'Shared daily, batch and monthly limits apply to research, generation, tracking and analysis',
            'AI summaries refresh only on user request and show when they were last updated',
            'Saved research and generation requests prevent duplicate paid work on reload',
            'Question activation previews weekly search fees, with tokens and analysis charged separately'
          ]
        },
        {
          title: 'History & Cleanup Reliability',
          description: 'Improved historical reporting, refresh recovery and website deletion.',
          icon: <Trophy className="h-5 w-5 text-orange-600" />,
          details: [
            'Historical citation trends retain earlier observations and distinguish model changes',
            'Saved refresh status makes interrupted or incomplete scheduled work visible',
            'Deleted websites stop tracking immediately while their saved data is removed in the background',
            'Cleanup resumes after restarts and reduces batch sizes after database timeouts',
            'Spending records remain intact when a website is deleted'
          ]
        }
      ]
    },
    {
      version: '1.3.0',
      date: 'October 6, 2024',
      title: 'Team Management & Domain Citations',
      type: 'feature',
      items: [
        {
          title: 'Domain Citation Viewer',
          description: 'View all citations for any domain with detailed information and export capability',
          icon: <Trophy className="h-5 w-5 text-blue-600" />,
          details: [
            'Replaced sample citation link with "View All" button',
            'Modal popup showing all citations for selected domain',
            'Export domain citations to CSV with full details',
            'See title, URL, snippet, classification, and position for each citation',
            'Filter by website when viewing domain citations'
          ]
        },
        {
          title: 'Website Filter for Leading Domains',
          description: 'Filter leading domains by specific website or view all',
          icon: <Building2 className="h-5 w-5 text-purple-600" />,
          details: [
            'Dropdown to filter by individual website or view all',
            'Accurate citation counts per filtered website',
            'Updated total citations display'
          ]
        },
        {
          title: 'Extended Invitation Links',
          description: 'Team invitations now last 7 days instead of 1 hour',
          icon: <Mail className="h-5 w-5 text-green-600" />,
          details: [
            'Magic links expire after 7 days (up from ~1 hour)',
            'Resend invitation button for expired links',
            'Better error handling for expired invitations',
            'Toast notifications for resend success'
          ]
        },
        {
          title: 'Increased Citation Limit',
          description: 'Leading Domains now processes up to 5,000 citations',
          icon: <ArrowUp className="h-5 w-5 text-orange-600" />,
          details: [
            'Increased from 1,000 to 5,000 citations',
            'Better coverage for high-volume accounts',
            'Accurate total citation counts beyond limit'
          ]
        },
        {
          title: 'UI Polish',
          description: 'Improved chart colors and removed gradient heading',
          icon: <TrendingUp className="h-5 w-5 text-blue-600" />,
          details: [
            'Changed brand mentions line to blue for better contrast',
            'Cleaner dashboard heading without gradient',
            'Three distinct colors: green (owned), blue (brand mentions), orange (competitors)'
          ]
        }
      ]
    },
    {
      version: '1.2.0',
      date: 'October 6, 2024',
      title: 'AI Assistant & Dashboard Enhancements',
      type: 'feature',
      items: [
        {
          title: 'AI Chat Assistant',
          description: 'Ask questions about your citation data in natural language and get instant insights',
          icon: <Sparkles className="h-5 w-5 text-purple-600" />,
          details: [
            'Natural language queries converted to SQL automatically',
            'Conversation history for contextual follow-up questions',
            'Floating sparkles button for easy access',
            'Secure query execution with read-only access'
          ]
        },
        {
          title: 'Dashboard Report Export',
          description: 'Download comprehensive reports in CSV or JSON format',
          icon: <Download className="h-5 w-5 text-blue-600" />,
          details: [
            'Export all dashboard metrics and keyword performance',
            'CSV format for spreadsheet analysis',
            'JSON format for custom integrations',
            'Success toast notifications on download'
          ]
        },
        {
          title: 'Enhanced Dashboard UI',
          description: 'Polished interface with modern visual design',
          icon: <TrendingUp className="h-5 w-5 text-green-600" />,
          details: [
            'Gradient text headings with blue-purple-orange theme',
            'Trend indicators showing week-over-week changes',
            'Subtle drop shadows with colored hover effects',
            'Toast notification system for user feedback',
            'Improved citation trends chart with 3 distinct lines'
          ]
        },
        {
          title: 'Brand Mentions Accuracy Fix',
          description: 'Fixed calculation to show total brand mentions across all keywords',
          icon: <FileText className="h-5 w-5 text-red-600" />,
          details: [
            'Dashboard KPIs now sum brand_mention_count correctly',
            'Citation trends chart shows accurate brand mention totals',
            'Resolved discrepancy between top-level metrics and table data'
          ]
        }
      ]
    },
    {
      version: '1.1.0',
      date: 'October 2, 2024',
      title: 'Leading Domains Analytics',
      type: 'feature',
      items: [
        {
          title: 'Leading Domains Page',
          description: 'New page showing most cited domains across all tracked websites',
          icon: <Trophy className="h-5 w-5 text-yellow-600" />
        },
        {
          title: 'Sparkline Charts',
          description: 'Visual citation trends over time for each domain with mini line charts',
          icon: <TrendingUp className="h-5 w-5 text-blue-600" />
        },
        {
          title: 'Growth Rate Tracking',
          description: '7-day growth rate comparison showing which domains are trending up or down',
          icon: <ArrowUp className="h-5 w-5 text-green-600" />
        },
        {
          title: 'Average Position Metrics',
          description: 'See where each domain typically ranks in LLM responses (lower = more prominent)',
          icon: <FileText className="h-5 w-5 text-purple-600" />
        },
        {
          title: 'New Domain Detection',
          description: 'Automatic badges for domains first seen within the last 7 days',
          icon: <Sparkles className="h-5 w-5 text-green-600" />
        },
        {
          title: 'CSV Export',
          description: 'Export all domain analytics to CSV for reporting and analysis',
          icon: <Download className="h-5 w-5 text-gray-600" />
        }
      ]
    },
    {
      version: '1.0.0',
      date: 'September 20, 2024',
      title: 'Initial Release',
      type: 'major',
      description: `Careerminds Labs (GEO Dash) is an AI citation tracking platform that helps you monitor and optimize your brand's visibility in Large Language Model (LLM) responses.`,
      items: [
        {
          title: 'How It Works',
          description: 'Track your brand mentions across AI-powered search results by monitoring citations in LLM responses. The system uses OpenAI\'s Search API to query AI models and analyze where your brand appears in the results.',
          details: [
            'Add keywords relevant to your industry and brand',
            'System queries LLM with those keywords',
            'Analyzes citations and brand mentions in responses',
            'Tracks trends and competitive positioning over time'
          ]
        },
        {
          title: 'Core Features',
          description: 'Essential tools for LLM citation tracking and analysis',
          details: [
            'Multi-website tracking with organization-level insights',
            'Keyword management with category organization',
            'Automated citation classification (owned, brand mention, competitor)',
            'Brand mention counting within citation text',
            'Citation position tracking in LLM responses',
            'Historical trend analysis and reporting'
          ]
        },
        {
          title: 'Dashboard & Analytics',
          description: 'Comprehensive metrics and visualizations',
          details: [
            'Real-time citation counts and brand scores',
            'Brand mention share vs competitors',
            'Top competitor tracking',
            'Citation distribution by domain',
            'Keyword performance metrics',
            'Query run history and status'
          ]
        },
        {
          title: 'Query Research',
          description: 'Interactive AI search and analysis tools',
          details: [
            'Run custom queries with location targeting',
            'View detailed citation breakdowns',
            'Analyze brand positioning in responses',
            'Export results for reporting'
          ]
        },
        {
          title: 'Content Optimization',
          description: 'AI-powered content recommendations',
          details: [
            'Generate content briefs based on LLM citation analysis',
            'Get suggestions for improving brand visibility',
            'Identify content gaps and opportunities',
            'Understand what type of content gets cited'
          ]
        },
        {
          title: 'Automation',
          description: 'Scheduled monitoring and batch processing',
          details: [
            'Automated keyword refresh on custom schedules',
            'Batch processing for multiple keywords',
            'Background job processing',
            'Real-time status monitoring'
          ]
        },
        {
          title: 'Team Collaboration',
          description: 'Organization-level features',
          details: [
            'Multi-user support with authentication',
            'Organization-based data isolation',
            'Shared dashboards and insights',
            'Team member management'
          ]
        }
      ]
    }
  ];

  const getTypeBadge = (type) => {
    const badges = {
      major: <Badge className="bg-purple-600">Major Release</Badge>,
      feature: <Badge className="bg-blue-600">New Feature</Badge>,
      improvement: <Badge className="bg-green-600">Improvement</Badge>,
      bugfix: <Badge className="bg-red-600">Bug Fix</Badge>
    };
    return badges[type] || badges.feature;
  };

  return (
    <div className="container mx-auto p-6 max-w-5xl">
      <div className="mb-6">
        <h1 className="text-3xl font-bold mb-2">Release Notes</h1>
        <p className="text-muted-foreground">
          Latest updates, features, and improvements to Careerminds Labs
        </p>
      </div>

      <div className="space-y-6">
        {releases.map((release) => (
          <Card key={release.version}>
            <CardHeader>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-3">
                  <CardTitle className="text-2xl">Version {release.version}</CardTitle>
                  {getTypeBadge(release.type)}
                </div>
                <span className="text-sm text-muted-foreground">{release.date}</span>
              </div>
              <CardDescription className="text-lg font-semibold">{release.title}</CardDescription>
              {release.description && (
                <p className="text-sm text-muted-foreground mt-2">{release.description}</p>
              )}
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {release.items.map((item, index) => (
                  <div key={index} className="border-l-2 border-blue-500 pl-4">
                    <div className="flex items-start gap-3 mb-2">
                      {item.icon && <div className="mt-0.5">{item.icon}</div>}
                      <div className="flex-1">
                        <h3 className="font-semibold text-lg mb-1">{item.title}</h3>
                        <p className="text-sm text-muted-foreground mb-3">{item.description}</p>
                        {item.details && (
                          <ul className="space-y-1 ml-4">
                            {item.details.map((detail, idx) => (
                              <li key={idx} className="text-sm text-muted-foreground flex items-start gap-2">
                                <span className="text-blue-600 mt-1">•</span>
                                <span>{detail}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="mt-8 p-6 bg-muted rounded-lg">
        <h2 className="text-lg font-semibold mb-2">Coming Soon</h2>
        <p className="text-sm text-muted-foreground mb-4">
          Features and improvements we're working on:
        </p>
        <ul className="space-y-2">
          <li className="text-sm flex items-start gap-2">
            <span className="text-blue-600 mt-1">•</span>
            <span>Email alerts for new high-authority domain citations</span>
          </li>
          <li className="text-sm flex items-start gap-2">
            <span className="text-blue-600 mt-1">•</span>
            <span>Domain authority metrics and SEO insights</span>
          </li>
          <li className="text-sm flex items-start gap-2">
            <span className="text-blue-600 mt-1">•</span>
            <span>Advanced filtering and search across all data</span>
          </li>
          <li className="text-sm flex items-start gap-2">
            <span className="text-blue-600 mt-1">•</span>
            <span>API access for custom integrations</span>
          </li>
          <li className="text-sm flex items-start gap-2">
            <span className="text-blue-600 mt-1">•</span>
            <span>Competitor tracking and comparison reports</span>
          </li>
        </ul>
      </div>
    </div>
  );
};

export default ReleaseNotes;
